<template>
  <div>

    <Head :title="__(title)" />

    <div class="flex flex-col items-center justify-between gap-3 mb-4 md:flex-row ticket-filters">
      <search-input v-model="form.search" placeholder="Rechercher un numero"
        class="w-full max-w-md search" :disableReset="true"></search-input>
      <select-input v-model="form.direction" class="w-full md:w-1/3 mr-2">
          <option :value="null">Direction</option>
          <option :value="'outgoing'">Sortant</option>
          <option :value="'incoming'">Entrant</option>
        </select-input>
        <button class="ml-3 rtl:mr-3 text-gray-500 hover:text-gray-700 focus:text-indigo-500 text-sm" type="button" @click="reset">{{ __('Reset') }}</button>
    </div>

    <div class="overflow-x-auto bg-white rounded-md shadow">
      <table class="w-full whitespace-nowrap">
        <tbody>
          <tr class="font-bold text-left">
            <th class="px-6 pt-6 pb-4">{{ __('Date') }}</th>
            <th class="px-6 pt-6 pb-4">{{ __('Appelant') }}</th>
            <th class="px-6 pt-6 pb-4">{{ __('Destinataire') }}</th>
            <th class="px-6 pt-6 pb-4">{{ __('Statut') }}</th>
            <th class="px-6 pt-6 pb-4">{{ __('Direction') }}</th>
            <th class="px-6 pt-6 pb-4">{{ __('Durée') }}</th>
            <th class="px-6 pt-6 pb-4">{{ __('Note vocale enregistrée') }}</th>
            <th class="px-6 pt-6 pb-4">{{ __('Prix') }}</th>
          </tr>
          <tr v-for="call in filteredCalls" :key="call" :class="[
            call.cfw_record_url != null ? 'bg-green-50' : '',
            'focus-within:bg-gray-100 bg-gray-100'
          ]">
            <td class="border-t">
              <Link class="flex items-center px-6 py-4 focus:text-indigo-500">
              {{ call['start_time'] }}
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" tabindex="-1">
              {{ call['from'] }}
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" tabindex="-1">
              {{ call['to'] }}
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" tabindex="-1">
              <div :class="getCallStatusClass(call)"
                class="flex items-center justify-centerp-2 space-x-2 text-white rounded-full p-2">
                <div :class="{
                  'bg-green-200': (call.direction === 'outgoing' && call.status === 'answered') ||
                    (call.direction === 'incoming' && call.status === 'answered'), 'bg-red-200': call.status !== 'answered'
                }" class="w-2 h-2  rounded-full animate-ping"></div>
                <div>
                  {{
                    call['direction'] === 'outgoing'
                      ? (['answered', 'completed'].includes(call['status']) ? 'Répondu' : 'Non répondu')
                      : (['answered', 'completed'].includes(call['status']) ? 'Reçu' : 'Non reçu')
                  }}
                </div>
              </div>
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" tabindex="-1">
              {{ call['direction'] == 'outgoing' ? 'Sortant' : 'Entrant' }}
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" tabindex="-1">
              {{ call['duration'] }}
              </Link>
            </td>
            <td class="border-t">
                <div v-if="call['cfw_record_url'] != null">
                    <audio  controls :src="call['cfw_record_url']"></audio>
                </div>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" tabindex="-1">
              {{ call['cost'] }}
              </Link>
            </td>
          </tr>
          <tr v-if="calls.length === 0">
            <td class="px-6 py-4 border-t" colspan="4">Aucun appel trouvé</td>
          </tr>
        </tbody>
      </table>
    </div>
    <!-- <div v-if="pagination.links.length > 3"> -->
    <div v-if="pagination && pagination['links']" class="flex flex-wrap -mb-1 p-nat">
      <template v-for="(link, key) in pagination['links']">
        <div v-if="link.url === null" :key="key"
          class="px-4 py-3 mb-1 mr-1 text-sm leading-4 text-gray-400 border rounded" v-html="link.label" />
        <Link v-else :key="key + 1" class="px-4 py-3 mb-1 mr-1 text-sm leading-4 border rounded"
          :class="{ 'active': link.active }" :href="getLinkPage(link.url)"> {{ link.label }} </Link>
      </template>
    </div>
    <!-- </div> -->
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import SearchInput from '@/Shared/SearchInput'
import SelectInput from '@/Shared/SelectInput'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'

export default {
  components: {
    SearchInput,
    SelectInput,
    Head,
    Icon,
    Link,
    Pagination,
  },
  layout: Layout,
  props: {
    filters: Object,
    calls: Object,
    title: String,
    pagination: Object,
  },
  data() {
    return {
      filteredCalls: [],
      form: {
        search: this.filters?.search ?? null,
        direction: this.filters?.direction ?? null,
      }
    }
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
         this.filteredCalls = this.calls.filter(call => {
          const matchDirection = this.form.direction
            ? call.direction === this.form.direction
            : true; // si pas de filtre, on garde tout

          const matchSearch = this.form.search
            ? Object.values(call).some(value =>
              String(value).toLowerCase().includes(this.form.search.toLowerCase())
            )
            : true;

          return matchDirection && matchSearch;
        })
      }, 150)
    }
  },
  created() {
    this.filteredCalls = this.calls;
  },
  methods: {

    getCallStatusClass(call) {
      if (call.direction === 'outgoing') {
        return call.status === 'answered' ? 'bg-blue-500' : 'bg-yellow-500';
      } else {
        return call.status === 'answered' ? 'bg-green-500' : 'bg-red-500';
      }
    },
    reset() {
      this.form.direction = null;
      this.form.search = null;
    },
    getLinkPage(url) {
      // Si l'URL existe, extraire la page à partir de la chaîne après `page=`
      if (url) {
        const page = new URL(url).searchParams.get('page');
        return `?page=${page}`; // Générer l'URL relative avec la page
      }
      return ''; // Retourne une chaîne vide si l'URL est nulle
    }
  },
}
</script>
